import { getInitData } from './tg'

const BASE_URL = import.meta.env.VITE_API_BASE ?? '/api'

export class ApiError extends Error {
  status: number
  body?: unknown
  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.status = status
    this.body = body
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  }
  const initData = getInitData()
  if (initData) headers.Authorization = `tma ${initData}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const resp = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!resp.ok) {
    let data: unknown = null
    try { data = await resp.json() } catch {}
    throw new ApiError(resp.status, `${method} ${path} → ${resp.status}`, data)
  }
  if (resp.status === 204) return undefined as T
  return (await resp.json()) as T
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b),
  put: <T>(p: string, b?: unknown) => request<T>('PUT', p, b),
  delete: <T>(p: string) => request<T>('DELETE', p),
}

// --- typed endpoints + mocks (fallback when no backend) ---

export interface Me {
  telegram_id: number
  username?: string
  first_name: string
  expires_at: string | null
  days_left: number
  is_active: boolean
  device_limit: number
  devices_used: number
  subscription: {
    is_active: boolean
    days_left: number
    traffic_used_gb: number
    traffic_limit_gb: number
    device_limit: number
  } | null
}

export interface DeviceBinding {
  count: number
  model?: string | null
  os?: string | null
  last_seen?: string | null
}
export interface Device {
  id: number
  label: string
  platform: 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'other'
  added_at: string
  last_seen?: string
  is_online: boolean
  bound?: DeviceBinding | null
}

export interface Server {
  id: string
  name: string
  flag: string
  cc?: string
  address?: string
  tag: string
  ping_ms: number | null
  is_active: boolean
}

export interface Plan {
  id: number
  name: string
  price_rub: number
  price_stars: number
  duration_days: number
  traffic_gb: number
  device_limit: number
  is_popular?: boolean
}

export interface Payment {
  id: number
  amount_rub: number
  method: 'sbp' | 'stars' | 'crypto'
  status: 'pending' | 'paid' | 'failed' | 'refunded'
  plan_name: string
  created_at: string
}

export interface ReferralEvent {
  id: number
  username: string
  days: number
  created_at: string
}

export interface ReferralSummary {
  ref_link: string
  total_invited: number
  total_paid: number
  total_days_earned: number
  events: ReferralEvent[]
}

export interface Settings {
  language: 'ru' | 'en'
  notify_3d: boolean
  notify_1d: boolean
  notify_low_traffic: boolean
}

export interface FaqEntry {
  id: string
  category: 'install' | 'pay' | 'device' | 'other'
  q: string
  a: string
}

// Use the real backend whenever we run inside Telegram (have initData) or in a
// production build. Local browser preview without Telegram falls back to mocks.
const haveBackend = import.meta.env.PROD || !!getInitData()

export async function fetchMe(): Promise<Me> {
  if (!haveBackend) return mockMe
  return api.get<Me>('/me')
}
export async function fetchDevices(): Promise<Device[]> {
  if (!haveBackend) return mockDevices
  return api.get<Device[]>('/devices')
}
export async function fetchServers(): Promise<Server[]> {
  if (!haveBackend) return mockServers
  return api.get<Server[]>('/servers')
}
export async function fetchPlans(): Promise<Plan[]> {
  if (!haveBackend) return mockPlans
  return api.get<Plan[]>('/plans')
}
export async function fetchPayments(): Promise<Payment[]> {
  if (!haveBackend) return mockPayments
  return api.get<Payment[]>('/payments')
}
export async function fetchReferrals(): Promise<ReferralSummary> {
  if (!haveBackend) return mockReferrals
  return api.get<ReferralSummary>('/referrals')
}

export async function fetchSettings(): Promise<Settings> {
  if (!haveBackend) return mockSettings
  return api.get<Settings>('/settings')
}
export async function fetchFaq(): Promise<FaqEntry[]> {
  if (!haveBackend) return mockFaq
  return api.get<FaqEntry[]>('/faq')
}
export async function saveSettings(s: Partial<Settings>): Promise<Settings> {
  if (!haveBackend) {
    Object.assign(mockSettings, s)
    return mockSettings
  }
  return api.put<Settings>('/settings', s)
}

// --- mutations ---

export interface DeviceLink {
  happ_link: string
  sub_link: string
}

export interface PrimaryLink extends DeviceLink {
  device_id: number
}

/** The single stable Home key. Idempotent server-side — never spawns new keys. */
export async function fetchPrimaryLink(): Promise<PrimaryLink> {
  if (!haveBackend) {
    return { device_id: 1, happ_link: 'happ://add/https://hulumulu.ru/sub/DEMO', sub_link: 'https://hulumulu.ru/sub/DEMO' }
  }
  return api.get<PrimaryLink>('/primary-link')
}

export interface NewDevice extends Device {
  happ_link: string
  sub_link: string
}

export async function addDevice(label: string, platform: Device['platform']): Promise<NewDevice> {
  if (!haveBackend) {
    const d: NewDevice = {
      id: Math.floor(Math.random() * 1e6), label, platform,
      added_at: new Date().toISOString(), is_online: false,
      happ_link: 'happ://add/https://hulumulu.ru/sub/DEMO',
      sub_link: 'https://hulumulu.ru/sub/DEMO',
    }
    mockDevices.push(d)
    return d
  }
  return api.post<NewDevice>('/devices', { label, platform })
}

export async function deleteDevice(id: number): Promise<void> {
  if (!haveBackend) {
    const i = mockDevices.findIndex(d => d.id === id)
    if (i >= 0) mockDevices.splice(i, 1)
    return
  }
  return api.delete<void>(`/devices/${id}`)
}

export async function deviceLink(id: number): Promise<DeviceLink> {
  if (!haveBackend) {
    return { happ_link: 'happ://add/https://hulumulu.ru/sub/DEMO', sub_link: 'https://hulumulu.ru/sub/DEMO' }
  }
  return api.get<DeviceLink>(`/devices/${id}/link`)
}

// Self-service: clear the user's HWID bindings (new phone / reinstall).
export async function resetHwid(): Promise<{ ok: boolean; removed?: number }> {
  if (!haveBackend) return { ok: true, removed: 0 }
  return api.post<{ ok: boolean; removed?: number }>('/hwid/reset')
}

export interface PromoResult {
  valid: boolean
  code?: string
  discount_percent?: number
}

export async function validatePromo(code: string): Promise<PromoResult> {
  if (!haveBackend) {
    if (code.toUpperCase() === 'SPARTA') return { valid: true, code: 'SPARTA', discount_percent: 15 }
    return { valid: false }
  }
  return api.post<PromoResult>('/promo/validate', { code })
}

export interface CheckoutResult {
  kind: 'url' | 'done'
  url?: string
  status?: string
  payment_id?: number
  error?: string
}

export const checkout = (input: { plan_id: number; devices: number; method?: 'sbp' | 'crypto'; promo?: string }) =>
  api.post<CheckoutResult>('/checkout', input)

export const buyExtraDevice = () =>
  api.post<CheckoutResult>('/devices/extra', {})

export const INCLUDED_DEVICES = 3

// ---- partner / affiliate ----
export interface PartnerCommissionRow { username: string; amount_rub: number; created_at: string }
export interface PartnerPayoutRow { amount_rub: number; note?: string | null; created_at: string }
export interface PartnerInfo {
  status: 'none' | 'pending' | 'active' | 'rejected' | 'disabled'
  code?: string
  ref_link?: string
  commission_rate?: number
  buyer_discount?: number
  invited?: number
  conversions?: number
  total_earned_rub?: number
  accrued_rub?: number
  commissions?: PartnerCommissionRow[]
  payouts?: PartnerPayoutRow[]
}
export const fetchPartner = () => api.get<PartnerInfo>('/partner')
export const applyPartner = (note: string) =>
  api.post<{ status?: string; error?: string }>('/partner/apply', { note })

// --- quotes (single source of price truth — backend computes everything) ---

export interface PlanQuote {
  plan_id: number; plan_name: string; duration_days: number; plan_price: number
  included_devices: number; requested_devices: number; extra_devices: number
  day_rate: number; extra_cost: number; total: number
}
export interface DeviceQuote {
  count: number; day_rate: number; days_left: number; cost: number
}
export const fetchPlanQuote = (plan_id: number, devices: number) =>
  api.get<PlanQuote>(`/quote/plan?plan_id=${plan_id}&devices=${devices}`)
export const fetchDeviceQuote = (count = 1) =>
  api.get<DeviceQuote>(`/quote/device?count=${count}`)

// --- mocks ---

const mockMe: Me = {
  telegram_id: 5463598075,
  first_name: 'Иван',
  username: 'admin',
  expires_at: '2026-08-03T00:00:00Z',
  days_left: 46,
  is_active: true,
  device_limit: 3,
  devices_used: 2,
  subscription: {
    is_active: true,
    days_left: 46,
    traffic_used_gb: 0.4,
    traffic_limit_gb: 2,
    device_limit: 3,
  },
}

const mockDevices: Device[] = [
  { id: 1, label: 'iPhone 15 Pro', platform: 'ios', added_at: '2026-05-28', is_online: true, last_seen: '2026-06-03T22:30:00Z', bound: { count: 1, model: 'iPhone 15 Pro', os: 'iOS', last_seen: '2026-06-03T22:30:00Z' } },
  { id: 2, label: 'MacBook Pro', platform: 'macos', added_at: '2026-05-30', is_online: false, last_seen: '2026-06-02T19:14:00Z', bound: { count: 1, model: 'Mac', os: 'macOS', last_seen: '2026-06-02T19:14:00Z' } },
  { id: 3, label: 'Ноут жены', platform: 'windows', added_at: '2026-06-01', is_online: false, last_seen: null as unknown as string, bound: { count: 2, model: 'Pixel 8', os: 'Android', last_seen: '2026-06-01T10:00:00Z' } },
]

// Mirrors the real (filtered) server list: bridge + one DE exit shown to the user.
const mockServers: Server[] = [
  { id: 'gods-vpn-0', name: 'Германия #1', flag: '🇩🇪', cc: 'de', tag: 'REALITY · gRPC', ping_ms: 42, is_active: true },
]

const mockPlans: Plan[] = [
  { id: 4, name: 'Месяц', price_rub: 300, price_stars: 199, duration_days: 30, traffic_gb: 0, device_limit: 3 },
  { id: 5, name: '3 месяца', price_rub: 800, price_stars: 529, duration_days: 90, traffic_gb: 0, device_limit: 3, is_popular: true },
  { id: 6, name: '6 месяцев', price_rub: 1500, price_stars: 999, duration_days: 180, traffic_gb: 0, device_limit: 3 },
]

const mockPayments: Payment[] = [
  { id: 1830986, amount_rub: 800, method: 'sbp', status: 'paid', plan_name: '3 месяца', created_at: '2026-06-15T20:24:00Z' },
  { id: 1815033, amount_rub: 300, method: 'stars', status: 'paid', plan_name: 'Месяц', created_at: '2026-05-21T19:10:00Z' },
]

const mockReferrals: ReferralSummary = {
  ref_link: 'https://t.me/CicxdaVPN_bot?start=ref_5463598075',
  total_invited: 5,
  total_paid: 3,
  total_days_earned: 21,
  events: [
    { id: 1, username: '@alex', days: 7, created_at: '2026-06-18T14:20:00Z' },
    { id: 2, username: '@max_t', days: 7, created_at: '2026-06-10T09:15:00Z' },
    { id: 3, username: '@maria', days: 7, created_at: '2026-05-30T22:01:00Z' },
  ],
}

const mockSettings: Settings = {
  language: 'ru',
  notify_3d: true,
  notify_1d: true,
  notify_low_traffic: true,
}

const mockFaq: FaqEntry[] = [
  { id: '1', category: 'install', q: 'Как установить VPN на iPhone?', a: 'Открой раздел «устройства» → «добавить устройство». Откроется страница с инструкцией и кнопкой импорта в INCY. Нажми её — INCY откроется автоматически и добавит профиль.' },
  { id: '2', category: 'install', q: 'Как установить VPN на Android?', a: 'То же самое — в разделе «устройства» нажми «добавить». Скачай INCY из Google Play, открой нашу ссылку — приложение подхватит профиль.' },
  { id: '3', category: 'install', q: 'Как установить VPN на компьютер?', a: 'Для macOS/Windows: установи INCY для desktop с официального сайта, в разделе «устройства» добавь новый профиль с типом «компьютер» — получишь .conf файл или sub-ссылку.' },
  { id: '4', category: 'pay', q: 'Какие способы оплаты доступны?', a: 'СБП и криптовалюта (USDT). При первой оплате банк может запросить подтверждение.' },
  { id: '5', category: 'pay', q: 'Как использовать промокод?', a: 'На странице «тарифы» в шаге «промокод» введи код. Скидка применится автоматически до оформления.' },
  { id: '6', category: 'pay', q: 'Не пришла оплата — что делать?', a: 'Подожди 5-10 минут — иногда банки задерживают. Если не пришло — напиши в поддержку, укажи номер платежа из истории.' },
  { id: '7', category: 'device', q: 'Сколько устройств можно подключить?', a: 'В тариф включено 3 устройства. Можно докупить дополнительные в приложении. Каждое устройство получает свой профиль.' },
  { id: '8', category: 'device', q: 'Что делать если устройство потерялось?', a: 'В разделе «устройства» нажми на потерянный девайс → «отозвать». Профиль немедленно перестанет работать, остальные продолжат.' },
  { id: '9', category: 'other', q: 'Как работает приглашение друзей?', a: 'Пригласи друга по своей ссылке. Когда он оформит тариф — оба получите +7 дней бесплатно.' },
]
